<template>
    <div>
        <div v-if="$loadingRouteData"
            class="container -document-type">
            <loader></loader>
        </div>

        <div v-if="!$loadingRouteData"
            class="container -document-type">
            <ol class="breadcrumb">
                <li><a v-link="{name: 'home'}">Accueil</a></li>
                <li><a v-link="{name: 'document-types'}">Documents</a></li>
                <li>Ajouter un document</li>
            </ol>

            <div class="heading">
                <div class="f -gutter -a-center">
                    <div class="expand">
                        <h1>
                            <editable-text :value.sync="documentType.name"
                                        placeholder="Nom"></editable-text>
                        </h1>
                    </div>
                    <div>
                        <button class="btn btn-primary"
                                v-on:click.prevent="save"
                                :class="isValid ? '' : 'disabled'">
                            <loader :inline="true"
                                    v-show="saving"></loader>
                            Enregistrer
                        </button>
                    </div>
                </div>
            </div>

            <div class="body">
                <h2>Propriétés</h2>

                <div class="form-horizontal">
                    <div class="form-group">
                        <label for="dt-reusable" class="col-sm-3 control-label">
                            Réutilisable
                        </label>
                        <div class="col-sm-9 checkbox">
                            <input type="checkbox"
                                v-model="documentType.reusable"
                                id="dt-reusable"/>
                        </div>
                    </div>
                    <div class="form-group">
                        <label for="dt-reusable" class="col-sm-3 control-label">
                            Modèle
                        </label>
                        <div class="col-sm-9 checkbox">
                            <editable-file :info.sync="documentType.template"></editable-file>
                        </div>
                    </div>
                    <div class="form-group">
                        <label for="dt-function" class="col-sm-3 control-label">
                            Fonction
                        </label>
                        <div class="col-sm-9 checkbox">
                            <editable-choice
                                class="function"
                                id="dt-function"
                                placeholder="—"
                                :enable-reset="false"
                                :value.sync="documentType.function"
                                :options="functions"
                            ></editable-choice>
                        </div>
                    </div>
                    <div class="form-group">
                        <label class="col-sm-3 control-label">
                            Correspondances
                        </label>
                        <div class="col-sm-9 checkbox">
                            <table class="table table-striped">
                                <tr v-if="documentType.partner_ids.length">
                                    <th>Partenaire</th>
                                    <th>Code</th>
                                    <th></th>
                                </tr>
                                <tr
                                    v-for="(i, partner) in documentType.partner_ids"
                                    :key="i"
                                >
                                    <td>
                                        <editable-choice
                                            placeholder="Partenaire"
                                            :enable-reset="false"
                                            :value.sync="partner.partner"
                                            :options="partnerIds"
                                        ></editable-choice>
                                    </td>
                                    <td>
                                        <editable-text
                                            :value.sync="partner.code"
                                            placeholder="Code partenaire"
                                        ></editable-text>
                                    </td>
                                    <td>
                                        <button
                                            class="btn btn-link btn-sm"
                                            v-on:click.prevent="deletePartner(i)"
                                        >
                                            Supprimer
                                        </button>
                                    </td>
                                </tr>
                                <tr>
                                    <td colspan="3">
                                        <button
                                            class="btn btn-default btn-sm"
                                            v-on:click.prevent="addPartner"
                                        >
                                            Ajouter un partenaire
                                        </button>
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                    <div class="form-group">
                        <label for="dt-company" class="col-sm-3 control-label">
                            Compagnie
                        </label>
                        <div class="col-sm-9 checkbox">
                            <editable-choice v-if="companies"
                                            class="dt-company"
                                            id="dt-company"
                                            placeholder="Aucune"
                                            :enable-reset="true"
                                            :value.sync="documentType.company"
                                            :options="companies"></editable-choice>
                        </div>
                    </div>
                    <div class="form-group">
                        <label for="dt-contract-type" class="col-sm-3 control-label">
                            Type de contrat
                        </label>
                        <div class="col-sm-9 checkbox"
                            v-if="documentType.company">
                            <editable-choice v-if="companies"
                                            class="dt-company"
                                            id="dt-contract-type"
                                            placeholder="Aucun"
                                            :enable-reset="true"
                                            :value.sync="documentType.contract_type"
                                            :options="contractTypes"></editable-choice>
                        </div>
                        <div class="col-sm-9 checkbox"
                            v-if="!documentType.company">
                            <em>Choisir une compagnie</em>
                        </div>
                    </div>
                    <div class="form-group">
                        <label for="dt-reusable" class="col-sm-3 control-label">
                            Archivé
                        </label>
                        <div class="col-sm-9 checkbox">
                            <input type="checkbox"
                                v-model="documentType.archived"
                                id="dt-archived"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<!--suppress JSCheckFunctionSignatures -->
<script type="text/ecmascript-6">
    import router from '../router';
    import Loader from '../components/Loader';
    import EditableText from '../components/editable/EditableText';
    import {getAllCompaniesMap} from '../api/companies';
    import {
        partnerIds,
        functions,
        emptyDocumentType,
        create as createDocType,
        fetch as fetchDocType,
        update as updateDocType,
        clearListCache
    } from '../api/document_types';
    import {asyncAction, tryOr404} from '../utils';
    import {manager as msgManager} from '../messages';
    import EditableChoice from '../components/editable/EditableChoice';
    import EditableFile from '../components/editable/EditableFile';

    export default {
        components: {
            EditableChoice,
            EditableFile,
            Loader,
            EditableText
        },
        data() {
            return {
                choices: {},
                msgManager,
                saving: false,
                companiesDb: {},
                documentType: {}
            };
        },
        route: {
            data: tryOr404(async function (transition) {
                let documentType;

                if (transition.to.name === 'document-types-edit') {
                    documentType = await fetchDocType(transition.to.params.id);
                } else {
                    documentType = emptyDocumentType();
                }

                return {
                    companiesDb: await getAllCompaniesMap(),
                    documentType: documentType
                };
            })
        },
        computed: {
            isName() {
                return this.documentType && this.documentType.name;
            },
            isInActiveseed() {
                return this.documentType.display_in_activeseed ||
                    this.documentType.required_in_activeseed;
            },

            isActiveseedValid() {
                if (this.isInActiveseed) {
                    return this.documentType.activeseed_name &&
                        this.documentType.activeseed_description;
                }
                return true;
            },
            isValid() {
                return this.isName && this.isActiveseedValid;
            },
            companies() {
                if (!this.companiesDb) {
                    return [];
                }

                const out = [];

                for (const k in this.companiesDb) {
                    if (this.companiesDb.hasOwnProperty(k)) {
                        out.push({
                            value: k,
                            label: this.companiesDb[k].name
                        });
                    }
                }

                return out;
            },
            contractTypes() {
                if (!this.companiesDb ||
                        !this.documentType.company ||
                        !this.companiesDb.hasOwnProperty(this.documentType.company)) {
                    return [];
                }

                const out = [];
                const types = this.companiesDb[this.documentType.company].types;

                for (const k in types) {
                    if (types.hasOwnProperty(k)) {
                        out.push({
                            value: k,
                            label: types[k].name
                        });
                    }
                }

                return out;
            },

            functions,

            partnerIds
        },
        watch: {
            'documentType.contract_type': function () {
                this.choices[this.documentType.company] = this.documentType.contract_type;
            },
            'documentType.company': function () {
                this.documentType.contract_type = this.choices[this.documentType.company];
            }
        },
        methods: {
            /**
             * Adds a partner to the list of mappings
             */
            addPartner() {
                this.documentType.partner_ids.push({
                    partner: 'activeseed',
                    code: ''
                });
            },

            /**
             * Deletes a partner from the list of mappings
             * @param remove_i {Number} Index to be removed
             */
            deletePartner(remove_i) {
                const out = [];

                for (let i = 0; i < this.documentType.partner_ids.length; i += 1) {
                    if (i !== remove_i) {
                        out.push(this.documentType.partner_ids[i]);
                    }
                }

                this.documentType.partner_ids = out;
            },

            save: asyncAction('saving', async function() {
                if (this.documentType.id) {
                    this.documentType = await updateDocType(this.documentType);
                    this.msgManager.push('Succès.', 'Sauvegarde réussie.', 'success');
                } else {
                    this.documentType = await createDocType(this.documentType);
                    this.msgManager.push('Succès.', 'Création réussie.', 'success');
                    router.replace({
                        name: 'document-types-edit',
                        params: {
                            id: this.documentType.id
                        }
                    });
                }

                clearListCache();
            })
        }
    };
</script>



// WEBPACK FOOTER //
// DocumentType.vue?8a92a5d8